/**
 * The clear Graph (ADR-0071): what the Builder shows for each step and arrow, in a structured
 * form the console translates.
 *
 * - `stepBadge`: what a step uses ("Python code", "Web browser", "AI model", …) instead of its
 *   node type;
 * - `stepAction`: "What it does" (runs recipe.py in mode ‘understand’, opens the pages from a
 *   step with page.js, …) and the key values that matter (the mode, the result format);
 * - `stepInputs`: what it receives, field by field, and where each comes from;
 * - `stepLimits`: limits and technical settings (time, memory, internet, ids), kept collapsed;
 * - `agentFiles` / `stepFiles`: the code a step runs (agent files, or code inline in a step);
 * - `edgeRole`: the normal path, a "didn't work" fallback, or a choice between paths;
 * - `stepOrder` and `clearLayout`: step numbers and a top-to-bottom layout along the normal path.
 */
import type { RegistryTool, Text } from "../types";
import { editorEdges, type EditorEdge } from "./editor";
import { describeCondition } from "./humanise";
import { referencedPaths, pathSegments } from "./datamap";
import { formulaRefs } from "./formula";
import { templateKey } from "./plain";
import type { AgentSpec, SpecNode } from "./types";

export type StepBadge = "python" | "browser" | "ai" | "knowledge" | "person" | "approval" | "decision" | "subagent" | "choose" | "parallel" | "join" | "code" | "system" | "wait" | "calc" | "loop" | "table" | "handover" | "finish";

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/** What the step uses, as a badge. */
export function stepBadge(node: SpecNode): StepBadge {
  switch (node.type) {
    case "tool":
      if (node.tool === "codeexec") return "python";
      if (node.tool === "browser") return "browser";
      return "system";
    case "llm":
      return "ai";
    case "retrieval":
      return "knowledge";
    case "human_input":
      return "person";
    case "human_approval":
      return "approval";
    case "decision":
      return "decision";
    case "subgraph":
      return "subagent";
    case "router":
      return "choose";
    case "parallel":
      return "parallel";
    case "join":
      return "join";
    case "python":
      return "code";
    case "wait":
      return "wait";
    case "calculate":
      return "calc";
    case "foreach":
      return "loop";
    case "table":
      return "table";
    case "handover":
      return "handover";
    default:
      return "finish";
  }
}

// ---------------------------------------------------------------- code files

export type CodeLang = "python" | "javascript" | "json" | "text";

export function codeLang(name: string, text = ""): CodeLang {
  if (/\.py$/i.test(name)) return "python";
  if (/\.(m?js|ts)$/i.test(name)) return "javascript";
  if (/\.json$/i.test(name)) return "json";
  if (/^\s*(async\s*)?\(?[\w,\s]*\)?\s*=>/.test(text) || /\bfunction\b/.test(text)) return "javascript";
  if (/^\s*(import |from |def |class )/m.test(text)) return "python";
  return "text";
}

/** Where code lives: an agent file, or inline in one step's argument. */
export type CodeSource = { kind: "file"; name: string } | { kind: "inline"; node: string; path: (string | number)[] };

export interface CodeFile {
  name: string;
  text: string;
  lang: CodeLang;
  source: CodeSource;
  /** Steps that use it (ids). */
  usedBy: string[];
}

const fileRef = (v: unknown): string | null => (isObj(v) && Object.keys(v).length === 1 && typeof v.$file === "string" ? v.$file : null);
const literalText = (v: unknown): string | null => (isObj(v) && "$literal" in v && typeof v.$literal === "string" ? v.$literal : null);

/** Names of the agent files a value refers to (`{"$file": name}`), in order. */
export function fileRefs(value: unknown): string[] {
  const name = fileRef(value);
  if (name) return [name];
  if (Array.isArray(value)) return value.flatMap(fileRefs);
  if (isObj(value) && !("$literal" in value)) return Object.values(value).flatMap(fileRefs);
  return [];
}

/** The agent's files (`spec.files`) and the steps that use each. */
export function agentFiles(spec: AgentSpec): CodeFile[] {
  const files = spec.spec.files ?? {};
  return Object.entries(files).map(([name, text]) => ({
    name,
    text,
    lang: codeLang(name, text),
    source: { kind: "file", name },
    usedBy: spec.spec.graph.nodes.filter((n) => fileRefs(n.args).includes(name)).map((n) => n.id),
  }));
}

/** Arguments that hold code (programs and scripts). */
const CODE_ARGS = new Set(["code", "script", "source", "program"]);

/** The code a step runs: its agent files, and code written inline in its arguments. */
export function stepFiles(spec: AgentSpec, node: SpecNode): CodeFile[] {
  const out: CodeFile[] = [];
  const files = spec.spec.files ?? {};
  const add = (name: string, text: string, source: CodeSource) => {
    if (out.some((f) => f.name === name)) return;
    const usedBy = source.kind === "file" ? spec.spec.graph.nodes.filter((n) => fileRefs(n.args).includes(name)).map((n) => n.id) : [node.id];
    out.push({ name, text, lang: codeLang(name, text), source, usedBy });
  };
  const args = isObj(node.args) ? node.args : {};
  // Program files first (the Code runner's `files`), then scripts, then the small main program.
  if (isObj(args.files)) {
    for (const [name, v] of Object.entries(args.files)) {
      const ref = fileRef(v);
      if (ref && typeof files[ref] === "string") add(ref, files[ref] as string, { kind: "file", name: ref });
      else if (typeof v === "string" || literalText(v) !== null) add(name, literalText(v) ?? (v as string), { kind: "inline", node: node.id, path: ["files", name] });
    }
  }
  for (const [arg, v] of Object.entries(args)) {
    if (!CODE_ARGS.has(arg)) continue;
    const ref = fileRef(v);
    if (ref && typeof files[ref] === "string") add(ref, files[ref] as string, { kind: "file", name: ref });
    else {
      const text = literalText(v) ?? (typeof v === "string" ? v : null);
      if (text !== null && !templateKey(text)) {
        const name = node.tool === "codeexec" && arg === "code" ? "main.py" : node.tool === "browser" ? "script.js" : arg;
        add(name, text, { kind: "inline", node: node.id, path: [arg] });
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------- what it does

/** Short structured description; the console words it (`dataMap:action.<kind>`). */
export type StepAction =
  | { kind: "runFile"; file: string; mode: string | null }
  | { kind: "runCode" }
  | { kind: "openPages"; from: string | null; script: string | null; many: boolean }
  | { kind: "useSystem"; tool: string; action: string }
  | { kind: "askAi"; prompt: "library" | "written" }
  | { kind: "search"; collection: string }
  | { kind: "askPerson" }
  | { kind: "approval"; from: string | null }
  | { kind: "decide"; options: number }
  | { kind: "subagent"; agent: string }
  | { kind: "choose"; routes: number }
  | { kind: "parallel"; branches: number }
  | { kind: "join"; strategy: string }
  | { kind: "code"; entrypoint: string }
  /** "Wait for" (ADR-0094): what the step waits for first (event, answer, time, a return). */
  | { kind: "wait"; for: "event" | "answer" | "time" | "at" | "return" | "nothing"; event: string; after: string }
  /** "Calculate" (ADR-0103): how many values it works out. */
  | { kind: "calc"; count: number }
  /** "For each item" (ADR-0104): the step that makes the list, and how many steps (or another agent). */
  | { kind: "foreach"; from: string | null; agent: string | null; steps: number }
  /** Table work (ADR-0105): read (an attached file or a value), match two tables, or group. */
  | { kind: "table"; op: "read" | "match" | "group"; file: boolean; keys: number }
  /** "Hand over to a person" (G-camp-cs-02). */
  | { kind: "handover" }
  | { kind: "finish" };

/** The step that makes a state path's root value, if any. */
export function writerOf(spec: AgentSpec, path: string | null): string | null {
  if (!path) return null;
  const root = String(pathSegments(path)[0] ?? "");
  // The AI's tool call is made by the AI step that offers systems.
  if (root === "tool_call") return spec.spec.graph.nodes.find((n) => n.type === "llm" && Array.isArray(n.tools) && n.tools.length > 0)?.id ?? null;
  for (const n of spec.spec.graph.nodes) {
    const out = typeof n.output_key === "string" && n.output_key ? n.output_key : null;
    const key =
      n.type === "tool" ? (out ?? `${n.id}_result`) : n.type === "llm" ? out : n.type === "retrieval" ? (out ?? "context") : n.type === "human_approval" ? `${n.id}_decision` : n.type === "subgraph" ? (out ?? "subgraph_result") : n.type === "decision" ? (out ?? n.id) : n.type === "join" ? n.id : n.type === "wait" ? (out ?? `${n.id}_result`) : out;
    if (key === root) return n.id;
    if (n.type === "calculate" && (n.values ?? []).some((c) => c.key === root)) return n.id;
    if (n.type === "foreach" && (out ?? `${n.id}_results`) === root) return n.id;
    if (n.type === "table" && (out ?? `${n.id}_table`) === root) return n.id;
  }
  return null;
}

export function stepAction(spec: AgentSpec, node: SpecNode): StepAction {
  const args = isObj(node.args) ? node.args : {};
  switch (node.type) {
    case "tool": {
      if (node.tool === "codeexec") {
        const files = stepFiles(spec, node).filter((f) => f.lang === "python" && f.name !== "main.py");
        const input = isObj(args.input) ? args.input : {};
        const mode = typeof input.step === "string" ? input.step : typeof input.mode === "string" ? input.mode : null;
        return files[0] ? { kind: "runFile", file: files[0].name, mode } : { kind: "runCode" };
      }
      if (node.tool === "browser") {
        const urls = templateKey(args.urls ?? args.url);
        const script = stepFiles(spec, node).find((f) => f.lang === "javascript");
        return { kind: "openPages", from: writerOf(spec, urls), script: script?.name ?? null, many: node.action === "read_pages" };
      }
      return { kind: "useSystem", tool: node.tool ?? "", action: node.action ?? "" };
    }
    case "llm":
      return { kind: "askAi", prompt: isObj(node.prompt) && typeof node.prompt.ref === "string" && node.prompt.ref ? "library" : "written" };
    case "retrieval":
      return { kind: "search", collection: node.collection ?? "" };
    case "human_input":
      return { kind: "askPerson" };
    case "human_approval":
      return { kind: "approval", from: writerOf(spec, typeof node.subject_key === "string" ? node.subject_key : null) };
    case "decision":
      return { kind: "decide", options: (node.options ?? []).length };
    case "subgraph":
      return { kind: "subagent", agent: typeof node.agent === "string" ? node.agent : "" };
    case "router":
      return { kind: "choose", routes: (node.routes ?? []).length };
    case "parallel":
      return { kind: "parallel", branches: (node.branches ?? []).length };
    case "join":
      return { kind: "join", strategy: typeof node.strategy === "string" ? node.strategy : "merge" };
    case "python":
      return { kind: "code", entrypoint: typeof node.entrypoint === "string" ? node.entrypoint : "" };
    case "wait": {
      const returns = spec.spec.graph.nodes.some((n) => n.type === "llm" && JSON.stringify(n.reply ?? {}).includes(`"wait":"${node.id}"`));
      const first = node.event ? "event" : node.answer ? "answer" : node.after ? "time" : node.at ? "at" : returns ? "return" : "nothing";
      return { kind: "wait", for: first, event: node.event?.name ?? "", after: node.after ?? "" };
    }
    case "calculate":
      return { kind: "calc", count: (node.values ?? []).length };
    case "table":
      return { kind: "table", op: node.op ?? "read", file: (node.op ?? "read") === "read" && !node.source, keys: (node.keys ?? []).length };
    case "foreach":
      return { kind: "foreach", from: writerOf(spec, node.items ?? null), agent: typeof node.agent === "string" && node.agent ? node.agent : null, steps: (node.steps ?? []).length };
    case "handover":
      return { kind: "handover" };
    default:
      return { kind: "finish" };
  }
}

// ---------------------------------------------------------------- inputs and limits

export interface StepInput {
  /** Path of the argument inside the step (`["input", "question"]`). */
  arg: (string | number)[];
  /** The state path it comes from, when it is a value from earlier. */
  ref: string | null;
  /** The step that makes it (null: the request, or set here). */
  from: string | null;
  /** A value set in the step itself. */
  value?: unknown;
}

/** Settings that are limits or technical (shown collapsed). */
const TECHNICAL = /(^|_)(timeout|timeout_s|memory|memory_mb|retries|output|stdin|headers|options|max_iterations)(_|$)|_(s|ms|mb)$/;

/** What a step receives: every value from earlier (and set here) in its arguments. */
export function stepInputs(spec: AgentSpec, node: SpecNode): StepInput[] {
  const out: StepInput[] = [];
  const visit = (v: unknown, path: (string | number)[]) => {
    const last = String(path[path.length - 1] ?? "");
    if (fileRef(v) || literalText(v) !== null) return;
    if (typeof v === "string") {
      if (CODE_ARGS.has(String(path[0])) && path.length === 1) return;
      const refs = referencedPaths(v);
      if (refs.length) refs.forEach((r) => out.push({ arg: path, ref: r, from: writerOf(spec, r) }));
      // The Code runner's mode is shown with what the step does, not as an input.
      else if (node.tool === "codeexec" && path.join(".").match(/^input\.(step|mode)$/)) return;
      else if (!TECHNICAL.test(last)) out.push({ arg: path, ref: null, from: null, value: v });
      return;
    }
    if (Array.isArray(v)) {
      v.forEach((x, i) => visit(x, [...path, i]));
      return;
    }
    if (isObj(v)) {
      if (path.length && path[0] === "files") return;
      for (const [k, x] of Object.entries(v)) visit(x, [...path, k]);
      return;
    }
    if (v !== undefined && v !== null && !TECHNICAL.test(last)) out.push({ arg: path, ref: null, from: null, value: v });
  };
  if (node.type === "tool") visit(node.args ?? {}, []);
  else if (node.type === "decision") visit(node.input ?? "{{ state.question }}", ["input"]);
  else if (node.type === "retrieval") visit(node.query ?? "{{ state.question }}", ["query"]);
  else if (node.type === "subgraph") visit(node.input_map ?? {}, []);
  else if (node.type === "llm") {
    const vars = isObj(node.prompt) && Array.isArray(node.prompt.variables) ? (node.prompt.variables as string[]) : [];
    // A saved prompt's placeholders bound to values (G-kn-23): each is an input of the step.
    const bound = isObj(node.prompt) && isObj(node.prompt.bindings) ? Object.entries(node.prompt.bindings as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === "string" && !!e[1]) : [];
    vars.filter((k) => !bound.some(([name]) => name === k)).forEach((k) => out.push({ arg: [k], ref: k, from: writerOf(spec, k) }));
    bound.forEach(([name, path]) => out.push({ arg: [name], ref: path, from: writerOf(spec, path) }));
  } else if (node.type === "wait") {
    if (node.event?.key) visit(node.event.key, ["event", "key"]);
    if (node.at) visit(node.at, ["at"]);
  } else if (node.type === "router") {
    // A rule-based "Choose a path" reads the values its rules test (S-sweep: it said "nothing").
    const seen = new Set<string>();
    (node.routes ?? []).forEach((r, i) => {
      for (const m of String(r.when ?? "").matchAll(/\bstate\.([A-Za-z_]\w*(?:\.[A-Za-z_]\w*|\[\d+\])*)/g)) {
        const ref = m[1] as string;
        if (seen.has(ref)) continue;
        seen.add(ref);
        out.push({ arg: ["routes", i], ref, from: writerOf(spec, ref) });
      }
    });
  } else if (node.type === "table") {
    for (const k of ["source", "left", "right"] as const) {
      const ref = node[k];
      if (typeof ref === "string" && ref) out.push({ arg: [k], ref, from: writerOf(spec, ref) });
    }
  } else if (node.type === "foreach" && node.items) {
    out.push({ arg: ["items"], ref: node.items, from: writerOf(spec, node.items) });
  } else if (node.type === "calculate") {
    // What its formulas read (ADR-0103); a value worked out earlier in the same step is its own.
    const own = new Set<string>();
    (node.values ?? []).forEach((c, i) => {
      for (const ref of formulaRefs(c.expr)) {
        const root = String(pathSegments(ref)[0] ?? ref);
        if (!own.has(root) && !out.some((x) => x.ref === ref)) out.push({ arg: ["values", i], ref, from: writerOf(spec, ref) });
      }
      own.add(c.key);
    });
  } else if (node.type === "human_approval" && typeof node.subject_key === "string") {
    out.push({ arg: ["subject_key"], ref: node.subject_key, from: writerOf(spec, node.subject_key) });
  }
  return out;
}

export interface StepLimit {
  key: string;
  value: unknown;
}

/** Limits and technical settings of a step (time, memory, result format, repeats). */
export function stepLimits(node: SpecNode, tools: readonly RegistryTool[] = []): StepLimit[] {
  const out: StepLimit[] = [];
  const args = isObj(node.args) ? node.args : {};
  for (const [k, v] of Object.entries(args)) if (TECHNICAL.test(k) && !isObj(v)) out.push({ key: k, value: v });
  if (node.tool === "codeexec") {
    if (!("timeout_s" in args)) out.push({ key: "timeout_s", value: 10 });
    if (!("memory_mb" in args)) out.push({ key: "memory_mb", value: 256 });
    out.push({ key: "internet", value: "none" });
  }
  const action = tools.find((t) => t.ref === node.tool)?.actions.find((a) => a.name === node.action);
  if (action) out.push({ key: "changes_data", value: action.side_effect });
  if (typeof node.max_iterations === "number") out.push({ key: "max_iterations", value: node.max_iterations });
  if (typeof node.output_key === "string" && node.output_key) out.push({ key: "output_key", value: node.output_key });
  if (typeof node.output_field === "string" && node.output_field) out.push({ key: "output_field", value: node.output_field });
  return out;
}

// ---------------------------------------------------------------- arrows

export type EdgeRole = "normal" | "fallback" | "choice";

/** The normal path (solid green), a "didn't work" fallback (dashed orange) or a choice. */
export function edgeRole(spec: AgentSpec, edge: EditorEdge): EdgeRole {
  if (edge.kind === "reject" || edge.kind === "low_confidence" || edge.kind === "limit" || edge.kind === "error" || edge.kind === "give_up") return "fallback";
  if (edge.kind === "route" || edge.kind === "default") return "choice";
  if (edge.kind === "branch") return "normal";
  const source = spec.spec.graph.nodes.find((n) => n.id === edge.source);
  if (source?.type === "decision") return "choice";
  const when = (edge.when ?? "").trim();
  if (!when) return "normal";
  // Starting with "not" says nothing on its own: `not exists(state.tool_call)` (the AI has its
  // answer) is an ordinary choice. "Didn't work" is read from what the condition means.
  // `not state.x`: x is missing or empty, the way out when the other arrows check x.
  const bare = /^not\s+state\.([\w.]+)$/.exec(when);
  if (bare) return siblingChecks(spec, edge, bare[1] as string) ? "fallback" : "choice";
  const c = describeCondition(spec, when);
  if (c.kind === "clauses") {
    const ops = c.clauses.map((x) => x.op);
    // A step that did not work, or a person who said no.
    if (ops.every((o) => o === "not_ok" || o === "rejected")) return "fallback";
    // A value that is missing, where the other arrows from the step check that value: the way
    // out when the step could not make it (e.g. "message not understood").
    if (ops.every((o) => o === "not_ok" || o === "rejected" || o === "empty") && c.clauses.every((x) => x.op !== "empty" || siblingChecks(spec, edge, x.ref.path))) return "fallback";
    if (ops.every((o) => o === "ok" || o === "filled" || o === "approved")) return "normal";
  }
  return "choice";
}

/** Whether another arrow from the same step compares the value at `path` (or a field of it). */
function siblingChecks(spec: AgentSpec, edge: EditorEdge, path: string): boolean {
  return editorEdges(spec).some((o) => {
    if (o.id === edge.id || o.source !== edge.source || !o.when) return false;
    const c = describeCondition(spec, o.when);
    return c.kind === "clauses" && c.clauses.some((x) => x.op !== "empty" && x.op !== "filled" && (x.ref.path === path || x.ref.path.startsWith(`${path}.`) || path.startsWith(`${x.ref.path}.`)));
  });
}

/** The arrow's own plain label (`label` on an explicit edge), if any. */
export function edgeLabel(spec: AgentSpec, edge: EditorEdge): Text | null {
  if (edge.kind !== "edge") return null;
  const i = Number(edge.id.split(":")[1]);
  const label = spec.spec.graph.edges?.[i]?.label;
  return label && (label.en || label.ar) ? label : null;
}

// ---------------------------------------------------------------- numbers and layout

/** Steps in reading order: as laid out, top to bottom, then along each row (end steps excluded). */
export function stepOrder(spec: AgentSpec): string[] {
  const pos = clearLayout(spec);
  return spec.spec.graph.nodes
    .filter((n) => n.type !== "end")
    .map((n) => n.id)
    .sort((a, b) => (pos[a]?.y ?? 0) - (pos[b]?.y ?? 0) || (pos[a]?.x ?? 0) - (pos[b]?.x ?? 0));
}

export const CLEAR_NODE = { width: 340, gapX: 380, gapY: 80, height: 150, endHeight: 48 };

/**
 * Top-to-bottom layout: a step sits one row below the furthest step that leads to it (longest
 * path, ignoring loops back), so a fallback arrow that skips ahead runs down the side. Rows are
 * as tall as their tallest step (`heights`, as measured on screen; an estimate until then) plus a
 * gap for the arrow's label, so boxes never overlap whatever the number of steps.
 */
export function clearLayout(spec: AgentSpec, heights: Record<string, number> = {}): Record<string, { x: number; y: number }> {
  const g = spec.spec.graph;
  const edges = editorEdges(spec);
  const succ = new Map<string, string[]>();
  for (const e of edges) succ.set(e.source, [...(succ.get(e.source) ?? []), e.target]);
  // Back edges (loops) from a depth-first walk.
  const back = new Set<string>();
  const state = new Map<string, number>();
  const dfs = (id: string) => {
    state.set(id, 1);
    for (const t of succ.get(id) ?? []) {
      if (state.get(t) === 1) back.add(`${id}>${t}`);
      else if (!state.has(t)) dfs(t);
    }
    state.set(id, 2);
  };
  if (g.nodes.some((n) => n.id === g.entry)) dfs(g.entry);
  for (const n of g.nodes) if (!state.has(n.id)) dfs(n.id);
  const depth = new Map<string, number>(g.nodes.map((n) => [n.id, 0]));
  for (let i = 0; i < g.nodes.length + 1; i++) {
    let changed = false;
    for (const e of edges) {
      if (back.has(`${e.source}>${e.target}`) || !depth.has(e.target)) continue;
      const d = (depth.get(e.source) ?? 0) + 1;
      if (d > (depth.get(e.target) ?? 0)) {
        depth.set(e.target, d);
        changed = true;
      }
    }
    if (!changed) break;
  }
  const rows = new Map<number, string[]>();
  for (const n of g.nodes) {
    const d = depth.get(n.id) ?? 0;
    rows.set(d, [...(rows.get(d) ?? []), n.id]);
  }
  // Finish and the wait step of "Several at once" are small boxes.
  const small = (id: string) => ["end", "join"].includes(g.nodes.find((n) => n.id === id)?.type ?? "");
  const heightOf = (id: string) => heights[id] ?? (small(id) ? CLEAR_NODE.endHeight : CLEAR_NODE.height);
  const out: Record<string, { x: number; y: number }> = {};
  let y = 0;
  for (const d of [...rows.keys()].sort((a, b) => a - b)) {
    const ids = rows.get(d) ?? [];
    ids.forEach((id, i) => {
      const w = small(id) ? 240 : CLEAR_NODE.width;
      // Centred on the column of normal-width steps.
      out[id] = { x: (i - (ids.length - 1) / 2) * CLEAR_NODE.gapX + (CLEAR_NODE.width - w) / 2, y };
    });
    y += Math.max(...ids.map(heightOf)) + CLEAR_NODE.gapY;
  }
  return out;
}

// ---------------------------------------------------------------- the Read view

export interface ShapeField {
  key: string;
  /** Plain name: the schema's `x-label` (both languages) or `title`, else null. */
  title: Text | null;
  description: string | null;
  /** Bookkeeping the agent uses itself (`x-internal: true`), folded in the Read view. */
  internal: boolean;
  schema: Record<string, unknown>;
}

/** The top-level fields of what a step makes: by `x-order` when given (stored specs sort their
 * keys), then in schema order. */
export function shapeFields(schema: unknown): ShapeField[] {
  if (!isObj(schema) || !isObj(schema.properties)) return [];
  const order = (s: unknown) => (isObj(s) && typeof s["x-order"] === "number" ? s["x-order"] : Number.MAX_SAFE_INTEGER);
  const entries = Object.entries(schema.properties).map((e, i) => [e, i] as const);
  entries.sort(([[, a], i], [[, b], j]) => order(a) - order(b) || i - j);
  return entries.map(([[key, s]]) => {
    const p = isObj(s) ? s : {};
    const xl = isObj(p["x-label"]) ? p["x-label"] : null;
    const en = xl && typeof xl.en === "string" ? xl.en : null;
    const ar = xl && typeof xl.ar === "string" ? xl.ar : null;
    const title: Text | null = en || ar ? { en: en ?? ar ?? "", ar: ar ?? en ?? "" } : typeof p.title === "string" && p.title ? { en: p.title, ar: p.title } : null;
    return { key, title, description: typeof p.description === "string" ? p.description : null, internal: p["x-internal"] === true, schema: p };
  });
}

/** The value at an argument path inside a run value (`["input", "question"]`), if any. */
export function valueAt(value: unknown, path: readonly (string | number)[]): unknown {
  let cur: unknown = value;
  for (const seg of path) {
    if (Array.isArray(cur) && typeof seg === "number") cur = cur[seg];
    else if (isObj(cur)) cur = cur[String(seg)];
    else return undefined;
  }
  return cur;
}
